/**
 * User Dashboard - Shows tasks assigned to the logged-in user
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Priority } from '../types';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Clock, Calendar, CheckCircle2, Timer, ChevronDown, ChevronUp } from 'lucide-react';
import MilestoneTracker from '../components/MilestoneTracker';
import { useApp } from '../providers/AppProvider';
import { useOrganizationData } from '../hooks/useOrganizationData';
import SurfaceCard from '../components/SurfaceCard';
import QuickActionsBar from '../components/QuickActionsBar';
import { updateTask } from '../store';
import { addStandupNote } from '../services/dataService';
import { generateId } from '../utils/idGenerator';
import { WIDGET_MUTED_TEXT_CLASS, WIDGET_PANEL_CLASS, WIDGET_SUBTLE_PANEL_CLASS } from '../constants/widgetTheme';

export default function UserDashboard() {
  const navigate = useNavigate();
  const { state, loading, reload } = useApp();
  const currentUser = state?.currentUser ?? null;
  const [quickTaskId, setQuickTaskId] = useState<string | null>(null);
  const [quickPanelOpen, setQuickPanelOpen] = useState(false);
  const [milestonePanelOpen, setMilestonePanelOpen] = useState(false);
  const { tasks, users, projects } = useOrganizationData(state, {
    includeAllOrganizations: true,
  }) as {
    tasks: any[];
    users: any[];
    projects: any[];
  };

  const isExternalPartner = currentUser?.role === 'external_partner';
  
  const currentUserId = currentUser?.id || '';
  const currentUserIdentitySet = new Set([
    String(currentUser?.id || '').trim().toLowerCase(),
    String(currentUser?.email || '').trim().toLowerCase(),
  ]);
  
  // Get tasks assigned to current user
  const myTasks = tasks
    .filter(task => 
      (task.assigneeIds || []).some((assigneeId: string) =>
        currentUserIdentitySet.has(String(assigneeId || '').trim().toLowerCase())
      )
    )
    .sort((a, b) => {
      // Keep open/active tasks first in mobile and desktop lists
      const priorityOrder = { 'P0': 0, 'P1': 1, 'P2': 2, 'High': 3, 'Medium': 4, 'Low': 5 };
      const statusOrder = { 'In Progress': 0, 'Not Started': 1, 'In Review': 2, 'Blocked': 3, 'Done': 4, 'Completed': 5, 'Not Required': 6 };

      const closedStatuses = new Set(['Done', 'Completed', 'Not Required']);
      const aClosed = closedStatuses.has(String(a.status || ''));
      const bClosed = closedStatuses.has(String(b.status || ''));
      if (aClosed !== bClosed) return aClosed ? 1 : -1;
      
      const priorityDiff = (priorityOrder[a.priority as keyof typeof priorityOrder] || 10) - 
                          (priorityOrder[b.priority as keyof typeof priorityOrder] || 10);
      if (priorityDiff !== 0) return priorityDiff;
      
      return (statusOrder[a.status as keyof typeof statusOrder] || 10) - 
             (statusOrder[b.status as keyof typeof statusOrder] || 10);
    });

  useEffect(() => {
    if (myTasks.length === 0) {
      setQuickTaskId(null);
      return;
    }

    setQuickTaskId((prev) => {
      if (prev && myTasks.some((task) => task.id === prev)) {
        return prev;
      }
      return myTasks[0].id || null;
    });
  }, [myTasks]);

  const quickTask = useMemo(
    () => myTasks.find((task) => task.id === quickTaskId) || myTasks[0] || null,
    [myTasks, quickTaskId]
  );

  if (loading || !state) {
    return <div className="p-8 text-center">Loading dashboard...</div>;
  }

  if (!currentUser) {
    return <div className="p-8 text-center">User context not loaded</div>;
  }

  const stats = {
    notStarted: myTasks.filter(t => t.status === 'Not Started').length,
    inProgress: myTasks.filter(t => t.status === 'In Progress').length,
    inReview: myTasks.filter(t => t.status === 'In Review').length,
    done: myTasks.filter(t => t.status === 'Done').length,
    totalHours: myTasks.reduce((sum, t) => sum + (t.estimatedHours || 0), 0),
    loggedHours: myTasks.reduce((sum, t) => {
      const logged = t.timeLogs?.reduce((s, log) => s + log.hours, 0) || 0;
      return sum + logged;
    }, 0),
  };

  const statCards = [
    { label: 'Not Started', value: `${stats.notStarted}`, valueClass: 'text-foreground' },
    { label: 'In Progress', value: `${stats.inProgress}`, valueClass: 'text-primary' },
    { label: 'In Review', value: `${stats.inReview}`, valueClass: 'text-chart-4' },
    { label: 'Done', value: `${stats.done}`, valueClass: 'text-chart-2' },
    { label: 'Est. Hours', value: `${stats.totalHours.toFixed(0)}h`, valueClass: 'text-foreground' },
    { label: 'Logged', value: `${stats.loggedHours.toFixed(0)}h`, valueClass: 'text-chart-5' },
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'P0': return 'bg-destructive/10';
      case 'P1': return 'bg-chart-5/10';
      case 'P2': return 'bg-chart-5/10';
      case 'High': return 'bg-destructive/10';
      case 'Medium': return 'bg-primary/10';
      case 'Low': return 'bg-chart-2/10';
      default: return 'bg-muted-foreground/50';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Done': return 'bg-chart-2/10 text-chart-2';
      case 'In Progress': return 'bg-primary/10 text-primary';
      case 'In Review': return 'bg-chart-4/10 text-chart-4';
      case 'Blocked': return 'bg-destructive/10 text-destructive';
      default: return 'bg-muted text-foreground';
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-3 lg:pt-4 pb-6 max-w-7xl mx-auto">
      <div className="mb-4 sm:mb-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground">My Tasks</h1>
            <p className={`mt-1 text-xs sm:text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>
              Across all organizations • {myTasks.length} tasks assigned
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!isExternalPartner && (
              <>
                <Button variant="outline" onClick={() => navigate('/app/assign')} className="h-8 px-3 text-xs sm:text-sm">
                  Assign Tasks
                </Button>
                <Button onClick={() => navigate('/stakeholder')} className="h-8 px-3 text-xs sm:text-sm">
                  Stakeholder View
                </Button>
                <Button variant="outline" onClick={() => navigate('/app')} className="h-8 px-3 text-xs sm:text-sm">
                  Full System
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

        {/* Milestone Tracker - desktop */}
        <div className="mb-5 sm:mb-6 hidden lg:block">
          <MilestoneTracker
            tasks={tasks}
            users={users}
            canChangeMilestone={state.currentUser.role === 'stakeholder' || state.currentUser.role === 'admin'}
            activeMilestone={
              (localStorage.getItem('activeMilestone') as Priority) || 'P0'
            }
            onMilestoneChange={(milestone) => {
              localStorage.setItem('activeMilestone', milestone);
              window.location.reload(); // Reload to reflect changes
            }}
          />
        </div>

        {/* Milestone Tracker - mobile (collapsed by default) */}
        <Card className="mb-4 sm:mb-5 lg:hidden">
          <CardContent className="p-3">
            <button
              type="button"
              onClick={() => setMilestonePanelOpen((prev) => !prev)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground"
              title="Toggle active milestone"
            >
              {milestonePanelOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              Active Milestone
            </button>

            {milestonePanelOpen && (
              <div className="mt-3">
                <MilestoneTracker
                  tasks={tasks}
                  users={users}
                  canChangeMilestone={state.currentUser.role === 'stakeholder' || state.currentUser.role === 'admin'}
                  activeMilestone={
                    (localStorage.getItem('activeMilestone') as Priority) || 'P0'
                  }
                  onMilestoneChange={(milestone) => {
                    localStorage.setItem('activeMilestone', milestone);
                    window.location.reload();
                  }}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 sm:gap-3 mb-5 sm:mb-6">
          {statCards.map((stat) => (
            <SurfaceCard key={stat.label}>
              <CardContent className="p-2.5 sm:p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-[11px] sm:text-xs ${WIDGET_MUTED_TEXT_CLASS}`}>{stat.label}</span>
                  <span className={`text-sm sm:text-base font-semibold ${stat.valueClass}`}>{stat.value}</span>
                </div>
              </CardContent>
            </SurfaceCard>
          ))}
        </div>

        {/* Mobile quick actions using shared utility component */}
        {quickTask && (
          <Card className="mb-4 sm:mb-5 lg:hidden sticky top-14 z-20">
            <CardContent className="p-3 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => setQuickPanelOpen((prev) => !prev)}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground"
                  title="Toggle quick update panel"
                >
                  {quickPanelOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  Quick Update
                </button>
                <select
                  title="Select task for quick update"
                  value={quickTask.id || ''}
                  onChange={(e) => setQuickTaskId(e.target.value)}
                  className="h-8 max-w-[65%] rounded-md border border-input bg-background px-2 text-xs text-foreground"
                >
                  {myTasks.map((task) => (
                    <option key={task.id} value={task.id || ''}>
                      {task.id ? `${task.id}: ` : ''}{task.title}
                    </option>
                  ))}
                </select>
              </div>
              {quickPanelOpen && (
                <QuickActionsBar
                  sticky={false}
                  currentStatus={quickTask.status}
                  estimatedHours={quickTask.estimatedHours || 0}
                  onLogTime={async (hours, description) => {
                    if (!quickTask.id) return;
                    const timeLog = {
                      id: generateId('log', quickTask.id),
                      taskId: quickTask.id,
                      userId: currentUserId,
                      date: new Date().toISOString(),
                      hours,
                      description,
                    };
                    await updateTask(quickTask.id, { timeLogs: [...(quickTask.timeLogs || []), timeLog] });
                    if (state.currentUser?.email) await reload(state.currentUser.email);
                  }}
                  onAddNote={async (note) => {
                    if (!quickTask.id) return;
                    const now = new Date().toISOString();
                    await addStandupNote({
                      id: generateId('standup', quickTask.id),
                      organizationId: quickTask.organizationId,
                      projectId: quickTask.projectId,
                      authorId: currentUserId,
                      authorName: state.currentUser?.name,
                      content: note,
                      createdAt: now,
                      taskId: quickTask.id,
                      taskTitle: quickTask.title,
                      source: 'manual',
                    });
                    if (state.currentUser?.email) await reload(state.currentUser.email);
                  }}
                  onUpdateStatus={async (newStatus) => {
                    if (!quickTask.id) return;
                    await updateTask(quickTask.id, { status: newStatus as any });
                    if (state.currentUser?.email) await reload(state.currentUser.email);
                  }}
                />
              )}
            </CardContent>
          </Card>
        )}

        {/* Task List */}
        <div className="space-y-2.5 sm:space-y-3">
          {myTasks.length === 0 ? (
            <Card>
              <CardContent className="p-6 sm:p-8 text-center">
                <CheckCircle2 className="h-9 w-9 sm:h-10 sm:w-10 text-muted-foreground/50 mx-auto mb-3" />
                <p className={`text-sm sm:text-base ${WIDGET_MUTED_TEXT_CLASS}`}>No tasks assigned to you yet</p>
              </CardContent>
            </Card>
          ) : (
            myTasks.map(task => (
              <Card 
                key={task.id} 
                className={`${WIDGET_PANEL_CLASS} cursor-pointer transition-shadow hover:shadow-sm`}
                onClick={() => {
                  const project = projects.find(p => p.id === task.projectId);
                  if (project) {
                    navigate(`/app/projects/${project.id}/spreadsheet?assignee=${currentUserId}`);
                  } else {
                    navigate(`/task/${task.id}`);
                  }
                }}
              >
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-start gap-2 sm:gap-3">
                    {/* Priority indicator */}
                    <div className={`w-1 h-10 sm:h-12 rounded ${getPriorityColor(task.priority)}`} />
                    
                    <div className="flex-1 min-w-0">
                      {/* Header */}
                      <div className="flex items-start justify-between gap-2 sm:gap-4 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-1">
                            <Badge variant="outline" className="font-mono text-xs">
                              {task.id}
                            </Badge>
                            <Badge className={getStatusColor(task.status)}>
                              {task.status}
                            </Badge>
                            {task.priority && (
                              <Badge variant="outline" className="text-xs">{task.priority}</Badge>
                            )}
                          </div>
                          <h3 className="font-semibold text-sm sm:text-base">{task.title}</h3>
                          {task.description && (
                            <p className={`mt-1 line-clamp-2 text-xs ${WIDGET_MUTED_TEXT_CLASS}`}>
                              {task.description}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Metadata */}
                      <div className={`mt-2.5 flex flex-wrap items-center gap-2 text-xs sm:gap-3 ${WIDGET_MUTED_TEXT_CLASS}`}>
                        {task.estimatedHours && (
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                            <span>Est: {task.estimatedHours}h</span>
                          </div>
                        )}
                        {task.timeLogs && task.timeLogs.length > 0 && (
                          <div className="flex items-center gap-1">
                            <Timer className="h-3 w-3 sm:h-4 sm:w-4 text-chart-5" />
                            <span className="text-chart-5">
                              Logged: {task.timeLogs.reduce((sum, log) => sum + log.hours, 0).toFixed(1)}h
                            </span>
                          </div>
                        )}
                        {task.category && (
                          <div className="flex items-center gap-1">
                            <span className="font-medium">{task.category}</span>
                          </div>
                        )}
                        {task.standupNotes && task.standupNotes.length > 0 && (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                            <span>{task.standupNotes.length} standup notes</span>
                          </div>
                        )}
                      </div>

                      {/* Latest standup note */}
                      {task.standupNotes && task.standupNotes.length > 0 && (
                        <div className={`${WIDGET_SUBTLE_PANEL_CLASS} mt-2.5 rounded border p-2`}>
                          <div className="mb-1 text-xs font-medium text-primary">
                            Latest Update - {new Date(task.standupNotes[task.standupNotes.length - 1].createdAt || task.standupNotes[task.standupNotes.length - 1].date).toLocaleDateString()}
                          </div>
                          <p className="line-clamp-2 text-xs text-foreground/90">
                            {task.standupNotes[task.standupNotes.length - 1].content || task.standupNotes[task.standupNotes.length - 1].note}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
    </div>
  );
}